import React, {
    ComponentPropsWithRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { PlaceStms } from '../../components/PlaceStms'
import {
    AsgOrPlaceholder,
    Assignation,
    CommonValueType,
    CompareNAry,
    GO_BACK_INDEX_TYPE,
} from '../../context/types'
import { Button, Divider } from '@mui/material'
import { StatementCard } from '../../components/StatementCard'
import { Icons } from '../../assets/icons'
import { useTranslation } from '../../i18n/useTranslation'
import { useBinarySwap } from './useBinarySwap'
import _ from 'lodash'
import { nArySort } from './nary'
import { info, logException } from '../../services/sentryLogger'
import { stm2str, stms2str } from '../../utils/stm'
import { SessionData, RoundData } from '../../context/session/types'
import { useDelayedChoice } from './useDelayedChoice'
import { isMobilePortrait } from '../../utils'
import { MainTitle } from '../../components/MainTitle'

const N_ARY = 1 as const // 2 - 1
const CURRENT_STM_INDEX = 0
const COMPARED_STM_INDEX = 1

interface Props extends ComponentPropsWithRef<'div'> {
    goBack?: () => void
    commons: CommonValueType
}

const delayedStyles: React.CSSProperties = {
    // opacity: 0.5,
    pointerEvents: 'none',
    // backgroundColor: 'white',
}

export const NaryAssignation = (p: Props) => {
    const { commons } = p
    const [item2RankLocally, setItem2RankLocally] = useState<Assignation>(
        {} as Assignation
    )

    const comparedItems = useMemo<Assignation[]>(
        () => commons.items2compare,
        [commons.items2compare]
    )
    const resolver = useRef<((insertionIndex: number) => void) | null>(null)
    const [index2submit, setIndex2submit] = useState<number>(-1)

    const params = commons.state.params
    const t = useTranslation(commons)
    const { currentPos, swapIfNeeded } = useBinarySwap()
    const { delayedChoiceDuration } = params.assessmentParams
    const { isDelayed, startDelay } = useDelayedChoice(delayedChoiceDuration)

    const compareNAry = useCallback<CompareNAry<Assignation, 1>>(
        async (
            currentItem: Assignation,
            comparedItems: Assignation[],
            step: number
        ) => {
            info(
                '=== compareNary',
                comparedItems.length,
                {
                    currentItem: stm2str(currentItem),
                    step,
                },
                'comparedItems',
                stms2str(comparedItems)
            )

            // binary only !
            // FIXME requis?
            if (comparedItems.length !== 1 || !comparedItems[0]) {
                logException(
                    'compareNary - comparedItems.length !== 1',
                    comparedItems
                )
            }
            swapIfNeeded({
                topId: currentItem.id,
                bottomId: comparedItems[0].id,
            })

            // STATE on new comparison to make
            commons.setState((s: any) => ({ ...s, currentStmIndex: step - 1 }))
            commons.setItems2compare([...comparedItems]) // FIXME bug?
            setItem2RankLocally(currentItem)
            startDelay()
            return new Promise<number>((rs, _) => {
                resolver.current = (
                    insertionIndex: number | GO_BACK_INDEX_TYPE
                ) => {
                    info('compareNary resolver()', insertionIndex)
                    commons.setState((s: any) => ({ ...s, steps: s.steps + 1 }))
                    rs(insertionIndex)
                }
            })
        },
        [commons.asgnsForRound, startDelay]
    )

    // useEffect(() => {
    //     //  FIXME to remove
    //     if (!rankedItems.length) return
    //     info('NaryAssignation useEffect', rankedItems.length)
    //     setTimeout(() => {
    //         resolver.current && resolver.current(Math.random() > 0.5 ? 0 : 1)
    //     }, 250)
    // }, [rankedItems])

    useEffect(() => {
        info('NaryAssignation useEffect', commons.asgnsForRound.length)
        if (commons?.asgnsForRound?.length) {
            nArySort({
                currentStep: commons.state.currentStmIndex + 1,
                asgnsLeft2Rank: commons.asgnsLeft2Rank.current,
                asgnsRanked: commons.asgnsRanked.current,
                maxComparedSize: N_ARY, // FIXME n !== 1 (2-1)
                compareNAry,
                setAndSaveSessionData: commons.setAndSaveSessionData,
                currentRoundIndex: commons.state.currentRound - 1,
            })
                .then(commons.onSortRoundComplete)
                .catch((e) => logException('nArySort', 'useEffect', e))
        }
    }, [commons.asgnsForRound, compareNAry])

    const onPlaceStm = useCallback(
        (data: Assignation[]) => {
            const roundName = `round${commons.state.currentRound}`
            const insertionIndex = data.findIndex(
                (d) => d?.id === item2RankLocally?.id
            )
            info('submit', roundName, insertionIndex, stms2str(data))

            if (params.assessmentParams['submit_confirmation'] === 1) {
                setIndex2submit(insertionIndex)
            } else {
                resolver.current && resolver.current(insertionIndex)
            }
        },
        [item2RankLocally]
    )

    // computed
    const isBinary = useMemo(
        () => comparedItems.length <= 1,
        [comparedItems.length]
    ) // FIXME n !== 1 (2-1)
    const stmsOrPlaceholders = useMemo(
        () =>
            comparedItems.reduce((acc, curr) => [...acc, curr, null], [
                null,
            ] as AsgOrPlaceholder[]),
        [comparedItems]
    )
    const stms2Place = useMemo(
        () => (item2RankLocally ? [item2RankLocally] : []),
        [item2RankLocally]
    )

    const renderStm = (thisIndex: number, a: Assignation) => (
        <StatementCard
            showId={commons.state.params.assessmentParams['debug_mode'] === 1}
            big
            draggable={false}
            highlight={index2submit === thisIndex}
            emoji={!!params.assessmentParams['values_with_icons']}
            onlyLongPress
            readonly={isDelayed}
            disabled={isDelayed}
            style={{
                ...(isDelayed ? delayedStyles : {}),
                height: '20vh', // was 22
                width: '90vw',
            }}
            {...a}
            onEnter={() => {
                if (index2submit === -1) return
                if (resolver.current) {
                    setIndex2submit(-1)
                    resolver.current(index2submit)
                }
            }}
            onPickStm={async (id: string) => {
                const roundName = `round${commons.state.currentRound}`
                info(roundName, 'onPickStm', id, thisIndex)
                commons.setSessionData((p: SessionData) => {
                    const newData = {
                        ...p,
                        [roundName]: [
                            // @ts-ignore
                            ...p[roundName],
                            {
                                roundNumber: commons.state.currentRound,
                                stmIndex: commons.state.currentStmIndex,
                                stm: item2RankLocally,
                                comparedStms: _.cloneDeep(comparedItems),
                                choiceId: item2RankLocally?.id,
                            } as RoundData,
                        ],
                    } as SessionData
                    console.debug('setSessionData =', newData)
                    return newData
                })
                if (params.assessmentParams['submit_confirmation'] === 1) {
                    setIndex2submit(thisIndex)
                } else {
                    resolver.current && resolver.current(thisIndex)
                }
            }}
        />
    )
    const title = `${t('title_rank')} ${t(
        comparedItems.length == 1 ? 'subtitle_rank_2' : 'subtitle_rank_n' // FIXME reliquat binary vs nary
    )}`

    return (
        <div className="flex flex-1 flex-col justify-between items-center px-4">
            <MainTitle title={title} />
            {isBinary && (
                <div className="flex flex-1 flex-col justify-center">
                    {item2RankLocally?.id == currentPos?.topId
                        ? renderStm(CURRENT_STM_INDEX, item2RankLocally)
                        : renderStm(COMPARED_STM_INDEX, comparedItems[0])}
                    <Divider className="my-1" />
                    {/* {!isDelayed && <Divider className="my-4" />} */}
                    {/* {isDelayed && (
                        <LinearProgress
                            className="my-4 opacity-25"
                            color="inherit"
                        />
                    )} */}
                    {item2RankLocally?.id == currentPos?.bottomId
                        ? renderStm(CURRENT_STM_INDEX, item2RankLocally)
                        : renderStm(COMPARED_STM_INDEX, comparedItems[0])}
                </div>
            )}
            {!isBinary && (
                <PlaceStms
                    stmsOrPlaceholders={stmsOrPlaceholders}
                    stms2Place={stms2Place}
                    stmId2Highlight={item2RankLocally?.id}
                    submit={onPlaceStm}
                />
            )}
            <div className="flex flex-row w-full justify-around items-end">
                {params.assessmentParams['submit_confirmation'] === 1 && (
                    <Button
                        onClick={() => {
                            if (index2submit === -1) return
                            if (resolver.current) {
                                resolver.current(index2submit)
                                setIndex2submit(-1)
                            }
                        }}
                        disabled={index2submit === -1}
                        variant="contained"
                        color="primary"
                        className={`my-2 ${isMobilePortrait() ? 'w-1/2' : 'w-1/3'}`}
                        endIcon={<Icons.Next />}
                    >
                        {t('btn_submit')}
                    </Button>
                )}
            </div>
        </div>
    )
}
